import type { ApplicationSetting, ApplicationSettingDefinition, SettingEnumOption } from "../../models";

import { ApplicationSettingType } from "../../models";

/** Where a grid row's value comes from */
export type ValueSource = "companyWide" | "segment" | "inherited" | "default" | "none";

/** How a setting row relates to the segment being viewed */
export type RowStatus = "used" | "outranked" | "otherSegment";

/** The segment the values are shown for. With no ids set, values are company-wide */
export interface Segment {
	brandId?: number | null;
	channelId?: number | null;
	marketerId?: number | null;
	usStateId?: number | null;
	branchId?: number | null;
}

export interface DefinitionNode {
	definition: ApplicationSettingDefinition;
	/** FullName split on "." */
	parts: string[];
	parent?: DefinitionNode;
	children: DefinitionNode[];
}

export interface DefinitionTree {
	roots: DefinitionNode[];
	/** Every node, by lower-cased FullName */
	nodes: Map<string, DefinitionNode>;
	/** Settings (not containers) by lower-cased Name, for matching names to definitions */
	byName: Map<string, DefinitionNode[]>;
	/** Settings whose Name is a [Placeholder], which match any one segment */
	placeholders: DefinitionNode[];
}

export interface DefinitionMatch {
	node: DefinitionNode;
	/** True when the definition is the setting itself, false when it's a suffix used deeper */
	isExact: boolean;
}

export interface SettingGridRow {
	/** Lower-cased name: setting names are case-insensitive */
	key: string;
	name: string;
	friendlyName?: string | null;
	description?: string | null;
	notes?: string | null;
	dataType?: ApplicationSettingType | null;
	defaultValue?: string | null;
	/** For an Enum setting, its definition's options */
	enumOptions?: SettingEnumOption[] | null;
	value?: string | null;
	source: ValueSource;
	/** No definition matches the name, so the code probably doesn't read it */
	isOrphaned: boolean;
	/** The definition says the code reads it only company-wide, so the selected segment doesn't change its value */
	isCompanyWide: boolean;
	/** From the definition: the value is a credential (null when nobody's sure), the code needs a value, the code caches it */
	isSecret?: boolean | null;
	isRequired?: boolean;
	cached?: boolean;
	/** Lower-cased FullName of the matching definition */
	definitionName?: string;
	/** The row the value comes from, if any: one of this setting's rows, or a row of the setting it inherits from */
	winner?: ApplicationSetting;
	rows: ApplicationSetting[];
}

export interface GridFilter {
	/** FullNames of the selected containers, top level first */
	path: string[];
	/** FullName of the definition picked in the Setting dropdown */
	leaf?: string;
	search?: string;
	/** Only settings with at least one row, for any segment */
	specifiedOnly?: boolean;
	/** Only settings that no definition matches */
	orphanedOnly?: boolean;
	/**
	 * Only settings with a row for the segment: one that applies to it, or one for a narrower part of it.
	 * Company-wide rows don't count, except with no segment selected, when only they do
	 */
	pertinentOnly?: boolean;
	segment?: Segment;
	/** So office and marketer rows count as part of the segments they belong to */
	offices?: Office[];
}

/** An office and the segment it belongs to (BranchFilterItem) */
export interface Office {
	id: number;
	brandId?: number | null;
	channelId?: number | null;
	marketerId?: number | null;
	stateId?: number | null;
	/** Inactive offices aren't offered in the Office dropdown */
	Active?: boolean;
}

/** Office ids to their segments, and marketer ids to their brands */
interface SegmentLookup {
	offices: Map<number, Segment>;
	marketerBrands: Map<number, number>;
}

const lower = (value?: string | null) => (value ?? "").toLowerCase();

const splitName = (name: string) => name.trim().split(".");

export const isPlaceholder = (part: string) => part.startsWith("[") && part.endsWith("]");

const isContainer = (node: DefinitionNode) => node.definition.isContainer;

/** A [Placeholder] segment matches anything; others must match ignoring case */
const segmentMatches = (definitionPart: string, namePart: string) =>
	isPlaceholder(definitionPart) || lower(definitionPart) === lower(namePart);

/** Same number of segments, and each one matches */
const matchesExactly = (definitionParts: string[], nameParts: string[]) =>
	definitionParts.length === nameParts.length &&
	definitionParts.every((part, i) => segmentMatches(part, nameParts[i]));

/** The name sits at least one level below the definition's parent, and ends with the definition's own name */
function matchesAsSuffix(node: DefinitionNode, nameParts: string[]) {
	if (!node.definition.isSuffix || nameParts.length <= node.parts.length) return false;
	const suffixLength = splitName(node.definition.name).length;
	const parentLength = node.parts.length - suffixLength;
	return (
		matchesExactly(node.parts.slice(0, parentLength), nameParts.slice(0, parentLength)) &&
		matchesExactly(node.parts.slice(parentLength), nameParts.slice(nameParts.length - suffixLength))
	);
}

/** The name is below the node (one level or more) */
const isUnder = (nameParts: string[], node: DefinitionNode) =>
	nameParts.length > node.parts.length && node.parts.every((part, i) => segmentMatches(part, nameParts[i]));

const byDisplayName = (a: DefinitionNode, b: DefinitionNode) => a.definition.name.localeCompare(b.definition.name);

/** In cascade order: each pick narrows the dropdown lists after it */
export const segmentKeys = ["brandId", "channelId", "marketerId", "usStateId", "branchId"] as const;

/** No segment id is set */
export const isCompanyWide = (segment: Segment) => segmentKeys.every((key) => segment[key] == null);

/** Each segment id the row sets equals the segment's, as in SettingHelpers.MatchesSegment (TLP.Sync.Models) */
export const matchesSegment = (row: ApplicationSetting, segment: Segment) =>
	segmentKeys.every((key) => row[key] == null || row[key] === segment[key]);

/**
 * The row whose value applies, as SegmentableService picks it:
 * the company-wide row when no segment is set, otherwise the matching row with the highest SegmentPriority.
 */
export function winningRow(rows: ApplicationSetting[], segment?: Segment) {
	if (!segment || isCompanyWide(segment)) return rows.find(isCompanyWide);
	let winner: ApplicationSetting | undefined;
	for (const row of rows)
		if (matchesSegment(row, segment) && (!winner || row.segmentPriority > winner.segmentPriority)) winner = row;
	return winner;
}

function buildSegmentLookup(offices: Office[] = []): SegmentLookup {
	const lookup: SegmentLookup = { offices: new Map(), marketerBrands: new Map() };
	for (const { id, brandId, channelId, marketerId, stateId } of offices) {
		lookup.offices.set(id, { brandId, channelId, marketerId, usStateId: stateId });
		if (marketerId != null && brandId != null) lookup.marketerBrands.set(marketerId, brandId);
	}
	return lookup;
}

/** The row's segment plus what it implies: an office row's brand, channel, marketer and state, and a marketer row's brand */
function impliedSegment(row: Segment, lookup: SegmentLookup): Segment {
	if (row.branchId != null) return { ...lookup.offices.get(row.branchId), branchId: row.branchId };
	if (row.marketerId != null && row.brandId == null)
		return { ...row, brandId: lookup.marketerBrands.get(row.marketerId) };
	return row;
}

/**
 * Whether a row counts for Pertinent only. With a segment selected: the row applies to it, or is for a narrower part of it
 * (an office in the selected channel, say), and isn't company-wide. With no segment: the row is company-wide.
 */
function isPertinent(row: ApplicationSetting, segment: Segment | undefined, lookup: SegmentLookup) {
	if (!segment || isCompanyWide(segment)) return isCompanyWide(row);
	if (isCompanyWide(row)) return false;
	const implied = impliedSegment(row, lookup);
	const isNarrower = segmentKeys.every((key) => segment[key] == null || implied[key] === segment[key]); // has every id the segment sets
	return matchesSegment(row, segment) || isNarrower;
}

export function rowStatus(
	row: ApplicationSetting,
	winner: ApplicationSetting | undefined,
	segment?: Segment,
): RowStatus {
	if (row === winner) return "used";
	// With no segment only company-wide rows apply, and only one of those can exist
	return segment && !isCompanyWide(segment) && matchesSegment(row, segment) ? "outranked" : "otherSegment";
}

/** One label per segment id the row sets, such as ["Channel: Resi", "State: Texas"]; none for a company-wide row */
export function segmentParts(row: ApplicationSetting) {
	const parts = [
		["Brand", row.brand, row.brandId],
		["Channel", row.channel, row.channelId],
		["Marketer", row.marketer, row.marketerId],
		["State", row.usState, row.usStateId],
		["Office", row.branch, row.branchId],
	] as const;
	return parts.filter(([, , id]) => id != null).map(([label, name, id]) => `${label}: ${name ?? `#${id}`}`);
}

export function buildTree(definitions: ApplicationSettingDefinition[]): DefinitionTree {
	const nodes = new Map<string, DefinitionNode>();
	for (const definition of definitions)
		nodes.set(lower(definition.fullName), { definition, parts: splitName(definition.fullName), children: [] });

	const roots: DefinitionNode[] = [];
	const byName = new Map<string, DefinitionNode[]>();
	const placeholders: DefinitionNode[] = [];
	for (const node of nodes.values()) {
		const parent = nodes.get(lower(node.definition.parentFullName));
		if (parent) {
			node.parent = parent;
			parent.children.push(node);
		} else roots.push(node);

		if (isContainer(node)) continue;
		const name = lower(node.definition.name);
		if (name.startsWith("[")) placeholders.push(node);
		else byName.set(name, [...(byName.get(name) ?? []), node]);
	}

	roots.sort(byDisplayName);
	for (const node of nodes.values()) node.children.sort(byDisplayName);
	return { roots, nodes, byName, placeholders };
}

/**
 * Finds the definition a setting name belongs to, with the rules of ResolveSettingDefinition (TLP.Sync.Business):
 * an exact match first, otherwise the suffix of the closest ancestor; literal segments beat [Placeholder] ones.
 */
export function matchDefinition(tree: DefinitionTree, name: string): DefinitionMatch | undefined {
	const nameParts = splitName(name);
	const tails = nameParts.map((_, i) => lower(nameParts.slice(i).join(".")));
	const candidates = [...tails.flatMap((tail) => tree.byName.get(tail) ?? []), ...tree.placeholders];

	let best: DefinitionMatch | undefined;
	for (const node of candidates) {
		const isExact = matchesExactly(node.parts, nameParts);
		if (!isExact && !matchesAsSuffix(node, nameParts)) continue;
		if (!best || isBetterMatch({ node, isExact }, best)) best = { node, isExact };
	}
	return best;
}

function isBetterMatch(a: DefinitionMatch, b: DefinitionMatch) {
	if (a.isExact !== b.isExact) return a.isExact;
	if (a.node.parts.length !== b.node.parts.length) return a.node.parts.length > b.node.parts.length;
	return a.node.parts.filter(isPlaceholder).length < b.node.parts.filter(isPlaceholder).length;
}

/** Suffix settings declared on the node's ancestors, closest ancestor first, that the node doesn't redefine */
function inheritedSuffixes(node: DefinitionNode) {
	const ownNames = new Set(node.children.map((child) => lower(child.definition.name)));
	const suffixes: DefinitionNode[] = [];
	for (let ancestor = node.parent; ancestor; ancestor = ancestor.parent) {
		for (const child of ancestor.children) {
			const name = lower(child.definition.name);
			if (!child.definition.isSuffix || isContainer(child) || ownNames.has(name)) continue;
			ownNames.add(name);
			suffixes.push(child);
		}
	}
	return suffixes;
}

/** Containers to pick from below the given container (top-level containers when none is given) */
export function containerOptions(tree: DefinitionTree, parentFullName?: string) {
	if (!parentFullName) return tree.roots.filter(isContainer);
	return tree.nodes.get(lower(parentFullName))?.children.filter(isContainer) ?? [];
}

/** Settings to pick from at the given container: its own settings plus suffixes inherited from above */
export function settingOptions(tree: DefinitionTree, parentFullName?: string) {
	if (!parentFullName) return tree.roots.filter((node) => !isContainer(node));
	const parent = tree.nodes.get(lower(parentFullName));
	if (!parent) return [];
	return [...parent.children.filter((node) => !isContainer(node)), ...inheritedSuffixes(parent)].toSorted(
		byDisplayName,
	);
}

/**
 * One row per setting name the grid can show for the filters:
 * every setting defined under the selected container (including those with no row), the suffixes inherited at it,
 * and every existing setting row under it (deeper suffix uses, placeholder values, and orphans with no definition).
 */
export function buildGridRows(tree: DefinitionTree, settings: ApplicationSetting[], filter: GridFilter) {
	const settingsByName = groupByName(settings);

	const node = filter.path.length ? tree.nodes.get(lower(filter.path[filter.path.length - 1])) : undefined;
	if (filter.path.length && !node) return [];
	const gridRows = new Map<string, SettingGridRow>();

	function add(name: string, match?: DefinitionMatch) {
		const key = lower(name);
		if (!gridRows.has(key)) gridRows.set(key, settingRow(tree, settingsByName, name, match, filter.segment));
	}

	// Settings defined under the container; [Placeholder] templates aren't real names, their values come from the rows
	const stack = node ? [...node.children] : [...tree.roots];
	while (stack.length) {
		const current = stack.pop();
		stack.push(...current.children);
		if (!isContainer(current) && !current.definition.fullName.includes("["))
			add(current.definition.fullName, { node: current, isExact: true });
	}

	if (node)
		for (const suffix of inheritedSuffixes(node))
			add(`${node.definition.fullName}.${suffix.definition.name}`, { node: suffix, isExact: false });

	for (const [key, rows] of settingsByName) {
		if (gridRows.has(key)) continue;
		const name = rows[0].name;
		if (node && !isUnder(splitName(name), node)) continue;
		add(name, matchDefinition(tree, name));
	}

	return [...gridRows.values()].filter(matchesFilter(tree, filter)).toSorted((a, b) => a.name.localeCompare(b.name));
}

/** Setting rows by lower-cased name: setting names are case-insensitive */
export function groupByName(settings: ApplicationSetting[]) {
	const settingsByName = new Map<string, ApplicationSetting[]>();
	for (const setting of settings) {
		const key = lower(setting.name);
		settingsByName.set(key, [...(settingsByName.get(key) ?? []), setting]);
	}
	return settingsByName;
}

/**
 * One setting as a grid row: its value for the segment and where that comes from (its own row, a level it inherits
 * from, or its default), with its definition's details and every row it has
 */
export function settingRow(
	tree: DefinitionTree,
	settingsByName: Map<string, ApplicationSetting[]>,
	name: string,
	match = matchDefinition(tree, name),
	viewedSegment?: Segment,
): SettingGridRow {
	const key = lower(name);
	const rows = settingsByName.get(key) ?? [];
	const definition = match?.node.definition;
	// Only a definition known to be company-wide; unsure (null) ones are treated as segmented
	const isCompanyWideSetting = definition?.isCompanyWide === true;
	// The code reads a company-wide setting without a segment, so a row for the selected segment isn't used
	const segment = isCompanyWideSetting ? undefined : viewedSegment;
	const ownWinner = winningRow(rows, segment);
	const inherited = ownWinner ? undefined : inheritedRow(tree, settingsByName, rows[0]?.name ?? name, match, segment);
	const winner = ownWinner ?? inherited;
	const defaultValue = definition?.defaultValue;
	return {
		key,
		name: rows[0]?.name ?? name,
		friendlyName: definition?.friendlyName,
		description: definition?.description,
		notes: definition?.notes,
		dataType: definition?.dataType ?? winner?.dataType ?? rows[0]?.dataType,
		defaultValue,
		enumOptions: definition?.dataType === ApplicationSettingType.Enum ? definition.enumOptions : undefined,
		value: winner ? winner.value : defaultValue,
		source: inherited ? "inherited" : valueSource(winner, defaultValue),
		isOrphaned: !match,
		isCompanyWide: isCompanyWideSetting,
		isSecret: definition?.isSecret,
		isRequired: definition?.isRequired,
		cached: definition?.cached,
		definitionName: definition && lower(definition.fullName),
		winner,
		rows,
	};
}

/**
 * A setting's direct line in the hierarchy, such as for EnrollmentValidation.Duplicate.SendEmail:
 * above, the levels it falls back to, top level first (EnrollmentValidation.SendEmail); children, the settings one level
 * down that fall back to it first (EnrollmentValidation.Duplicate.CallbackNumber.SendEmail and the like). Siblings and
 * grandchildren aren't included. Both are empty for a setting that isn't read hierarchically, or has no definition.
 */
export function settingFamily(tree: DefinitionTree, settingsByName: Map<string, ApplicationSetting[]>, name: string) {
	const match = matchDefinition(tree, name);
	if (!match) return { above: [], children: [] };
	const above = fallbackNames(tree, name, match).toReversed();
	const nameParts = splitName(name);
	const ownName = nameParts.slice(-splitName(match.node.definition.name).length);
	const levels = nameParts.slice(0, nameParts.length - ownName.length);

	const candidates = new Map<string, string>();
	// One under each container directly below the setting's level; a [Placeholder] container's names come from rows
	const levelNode = levels.length ? containerNode(tree, levels) : undefined;
	for (const child of levelNode?.children ?? [])
		if (isContainer(child) && !isPlaceholder(child.definition.name)) {
			const childName = [...levels, child.definition.name, ...ownName].join(".");
			candidates.set(lower(childName), childName);
		}
	// Rows one level down, such as a [Placeholder] level filled in; a row's own casing wins
	for (const rows of settingsByName.values()) {
		const parts = splitName(rows[0].name);
		if (parts.length === nameParts.length + 1) candidates.set(lower(rows[0].name), rows[0].name);
	}

	const key = lower(name);
	const children = [...candidates.values()]
		.filter((candidate) => lower(fallbackNames(tree, candidate)[0]) === key)
		.toSorted((a, b) => a.localeCompare(b));
	return { above, children };
}

/** The container a setting's levels name, matching [Placeholder] containers too; a literal name wins over a placeholder */
function containerNode(tree: DefinitionTree, levels: string[]) {
	let best: DefinitionNode | undefined;
	for (const node of tree.nodes.values())
		if (isContainer(node) && matchesExactly(node.parts, levels))
			if (!best || node.parts.filter(isPlaceholder).length < best.parts.filter(isPlaceholder).length) best = node;
	return best;
}

/** How a level in the hierarchy gets its value: from its own row for the segment, or from the level above it */
export const chainLink = (row: SettingGridRow) =>
	row.winner && row.rows.includes(row.winner) ? "overrides" : "inheritedFrom";

/**
 * Lower-cased name of the level whose row gives the setting its value: its own row, the row of the level it inherits from,
 * or, when the value is a default, the top level of its chain
 */
export function valueLevel(row: SettingGridRow, chain: string[]) {
	if (chainLink(row) === "overrides") return row.key;
	if (row.source === "inherited" && row.winner) return lower(row.winner.name);
	return lower(chain[0] ?? row.name);
}

function valueSource(winner: ApplicationSetting | undefined, defaultValue?: string | null): ValueSource {
	if (winner) return isCompanyWide(winner) ? "companyWide" : "segment";
	return defaultValue != null ? "default" : "none";
}

/**
 * A suffix definition with a real name, such as EnrollmentValidation.Enabled: the setting is read at every level below
 * its parent, each level falling back to the one above. A [Placeholder] suffix only means any name can go there
 */
const isFallbackSuffix = (match: DefinitionMatch) =>
	match.node.definition.isSuffix && !isPlaceholder(match.node.definition.name);

/**
 * The names a setting falls back to when it has no row of its own, closest first, the way HeirarchicalSettingsService
 * reads it: drop the level just above the setting's own name, one at a time, while the shorter name is still a defined
 * setting. Only when a suffix definition is in that chain; any other setting is read by its full name only.
 * None for an orphan, which the code doesn't read.
 */
export function fallbackNames(tree: DefinitionTree, name: string, match = matchDefinition(tree, name)) {
	if (!match) return [];
	const nameParts = splitName(name);
	const ownName = nameParts.slice(-splitName(match.node.definition.name).length); // The setting's own name, after its levels
	const names: string[] = [];
	let fallsBack = isFallbackSuffix(match);
	for (let levels = nameParts.length - ownName.length - 1; levels > 0; levels--) {
		const levelName = [...nameParts.slice(0, levels), ...ownName].join(".");
		const levelMatch = matchDefinition(tree, levelName);
		if (!levelMatch) break;
		fallsBack ||= isFallbackSuffix(levelMatch);
		names.push(levelName);
	}
	return fallsBack ? names : [];
}

/** The row a setting inherits when it has none of its own for the segment: the first level it falls back to that has one */
function inheritedRow(
	tree: DefinitionTree,
	settingsByName: Map<string, ApplicationSetting[]>,
	name: string,
	match: DefinitionMatch | undefined,
	segment: Segment | undefined,
) {
	for (const levelName of fallbackNames(tree, name, match)) {
		const row = winningRow(settingsByName.get(lower(levelName)) ?? [], segment);
		if (row) return row;
	}
	return undefined;
}

function matchesFilter(tree: DefinitionTree, filter: GridFilter) {
	const leaf = filter.leaf ? tree.nodes.get(lower(filter.leaf)) : undefined;
	const leafKey = leaf && lower(leaf.definition.fullName);
	// A row also matches the Setting dropdown when its name ends with the setting's name (an override deeper down)
	const leafEnding = leaf && !isPlaceholder(leaf.definition.name) ? `.${lower(leaf.definition.name)}` : undefined;
	const search = lower(filter.search?.trim());
	const lookup = filter.pertinentOnly ? buildSegmentLookup(filter.offices) : undefined;

	return (row: SettingGridRow) => {
		if (filter.specifiedOnly && !row.rows.length) return false;
		if (filter.orphanedOnly && !row.isOrphaned) return false;
		if (lookup && !row.rows.some((setting) => isPertinent(setting, filter.segment, lookup))) return false;
		if (leaf && row.definitionName !== leafKey && !(leafEnding && `.${row.key}`.endsWith(leafEnding))) return false;
		if (search && ![row.name, row.friendlyName, row.description].some((text) => lower(text).includes(search)))
			return false;
		return true;
	};
}

const typeLabels: Record<ApplicationSettingType, string> = {
	[ApplicationSettingType.String]: "String",
	[ApplicationSettingType.Boolean]: "Boolean",
	[ApplicationSettingType.Int]: "Integer",
	[ApplicationSettingType.Enum]: "Enum",
	[ApplicationSettingType.StringList]: "String List",
};

export const settingTypeLabel = (type?: ApplicationSettingType | null) =>
	type == null ? "" : (typeLabels[type] ?? "");
